/**
 * m3 echo Frontend - Grounding Benchmark Detail Dialog
 *
 * Shows side-by-side comparison of responses with and without grounding.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import TimerIcon from '@mui/icons-material/Timer';
import TokenIcon from '@mui/icons-material/Numbers';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { GroundingBenchmarkResult } from '../../services/admin';

interface Props {
  open: boolean;
  onClose: () => void;
  result: GroundingBenchmarkResult | null;
}

const GroundingBenchmarkDetailDialog: React.FC<Props> = ({ open, onClose, result }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!result) return null;

  const tokensNo = result.tokens_no_grounding;
  const tokensWith = result.tokens_with_grounding;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { height: '90vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Grounding Vergleich</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {result.model_name}
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Prompt */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Prompt:
          </Typography>
          <Typography variant="body1">{result.prompt_text}</Typography>
        </Paper>

        {/* Side-by-side comparison */}
        <Grid container spacing={3}>
          {/* Without Grounding */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', minHeight: 400 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Ohne Grounding
                </Typography>
                {result.latency_no_grounding_ms && (
                  <Chip
                    icon={<TimerIcon />}
                    label={`${(result.latency_no_grounding_ms / 1000).toFixed(1)}s`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Stats */}
              {tokensNo && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<TokenIcon />}
                    label={`${tokensNo.total_tokens} Tokens`}
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    (In: {tokensNo.prompt_tokens} / Out: {tokensNo.completion_tokens})
                  </Typography>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Response */}
              <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    m: 0,
                  }}
                >
                  {result.response_no_grounding || 'Keine Antwort'}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* With Grounding */}
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                height: '100%',
                minHeight: 400,
                borderColor: 'primary.main',
                borderWidth: 2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Mit Grounding
                  </Typography>
                  <Chip
                    icon={<SearchIcon />}
                    label="Web Search"
                    size="small"
                    color="primary"
                  />
                </Box>
                {result.latency_with_grounding_ms && (
                  <Chip
                    icon={<TimerIcon />}
                    label={`${(result.latency_with_grounding_ms / 1000).toFixed(1)}s`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Stats */}
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {tokensWith && (
                  <>
                    <Chip
                      icon={<TokenIcon />}
                      label={`${tokensWith.total_tokens} Tokens`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Typography variant="caption" color="text.secondary">
                      (In: {tokensWith.prompt_tokens} / Out: {tokensWith.completion_tokens})
                    </Typography>
                  </>
                )}
                {result.sources && result.sources.length > 0 && (
                  <Chip
                    icon={<LinkIcon />}
                    label={`${result.sources.length} Quellen`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
                {result.brave_api_calls > 0 && (
                  <Chip
                    label={`${result.brave_api_calls} Brave calls`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Response */}
              <Box sx={{ maxHeight: 250, overflow: 'auto', mb: 2 }}>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    m: 0,
                  }}
                >
                  {result.response_with_grounding || 'Keine Antwort'}
                </Typography>
              </Box>

              {/* Sources */}
              {result.sources && result.sources.length > 0 && (
                <>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Verwendete Quellen:
                  </Typography>
                  <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                    {result.sources.map((source, idx) => (
                      <ListItem key={idx} disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <LinkIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Link
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                              {source.title || source.url}
                              <OpenInNewIcon fontSize="inherit" />
                            </Link>
                          }
                          secondary={source.snippet ? source.snippet.substring(0, 100) + '...' : null}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* Search Queries */}
              {result.search_queries && result.search_queries.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Suchanfragen:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {result.search_queries.map((query, idx) => (
                      <Chip
                        key={idx}
                        label={query}
                        size="small"
                        variant="outlined"
                        icon={<SearchIcon />}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Error Message */}
        {result.error_message && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'error.light' }}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              Fehler:
            </Typography>
            <Typography variant="body2">{result.error_message}</Typography>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroundingBenchmarkDetailDialog;
